/**
 * The cockpit's shared vocabulary: the seven stages of the value chain, the plain-English
 * definition of every term the UI shows, and the state helpers.
 *
 * Ported 1:1 from the POC (poc/product-cockpit/js/util.js) — the definitions ARE the
 * product here: a diagnostic nobody can read is a diagnostic nobody trusts. Keep them
 * jargon-free and keep every count next to its denominator.
 */

import type { StageName, StageState } from '@/services/productCockpitService';

export const STAGES: StageName[] = [
  'ingest', 'enrich', 'interpret', 'match', 'generate', 'publish', 'perform',
];

/** States that call for action. `unknown` is NOT one of them — unknown is not zero. */
export const ACTIONABLE: ReadonlySet<StageState> = new Set<StageState>(['partial', 'weak', 'poor']);

/** Worst-first severity rank, used to find the leftmost actionable stage of a row. */
export const RANK: Record<StageState, number> = {
  poor: 0, weak: 1, partial: 2, unknown: 3, by_design: 4, good: 5,
};

/** One-sentence, no-jargon definitions for every technical term the UI shows. */
export const DEFS: Record<string, string> = {
  ingest: 'INGEST — the product exactly as it arrived from the merchant feed: name, price, description, category, images.',
  enrich: 'ENRICH — the attributes the AI learned about this product (specs, features, materials), stored in the knowledge base.',
  interpret: 'INTERPRET — whether the category dictionary can explain those attributes: definitions, expected values, typical ranges.',
  match: 'MATCH — the customer benefits matched to this product from its category’s benefit pool.',
  generate: 'GENERATE — the AI-written page content (summary, FAQ, use cases…) produced from everything above.',
  publish: 'PUBLISH — the generated page going live: URLs observed on the site and translated languages.',
  perform: 'PERFORM — how the live page performs in search: Google impressions, clicks and average position (GSC).',
  provenance: 'PROVENANCE — how many of this product’s AI-learned values carry a RECORDED origin (which pipeline wrote them). A full bar means every value can be traced back to its source.',
  gsc: 'SEARCH (GSC) — Google Search Console data recorded for this product’s live page: impressions and average position over the recorded window.',
  origin_feed: 'This value came verbatim from the merchant product feed — no AI involved.',
  origin_ai: 'This value was learned by AI enrichment; the knowledge base recorded that origin.',
  origin_vendor: 'This value was extracted from the vendor / manufacturer’s own page.',
  origin_inferred: 'The AI inferred this value without a recorded source — treat it with care.',
  origin_missing: 'The value exists in the knowledge base but its origin was never recorded.',
  origin_feed_match: 'No origin was recorded for this value, but it is EXACTLY equal to a field of the merchant feed — so the cockpit shows it as feed-origin. Computed by full-value equality, not read from a recorded origin.',
  badge_key: '★ — a KEY attribute for this category: buyers decide on it, so the pipeline prioritises it.',
  badge_def: 'def — the category dictionary has a DEFINITION for this attribute.',
  badge_values: 'values — the dictionary lists the allowed / known values for this attribute.',
  badge_hints: 'hints — category research produced extraction hints for this attribute (how to read it off a product page).',
  badge_range: 'range — a typical min–max range is known for this attribute; values outside it are suspect.',
};

/** What each state means, shown on hover of a dot. */
export const STATE_DEFS: Record<StageState, string> = {
  good: 'good — this stage did its job for this product.',
  partial: 'partial — the stage ran but is short of what the category expects.',
  weak: 'weak — the stage ran but most of what it needed is missing.',
  poor: 'poor — the stage effectively did not happen for this product.',
  unknown: 'unknown — nothing was recorded to judge this stage. Unknown is NOT zero.',
  by_design: 'by design — expected in this state (e.g. not published yet). Not a failure.',
};

/** Every count states its total. */
export function frac(n: number, total: number, noun = ''): string {
  return `${n} of ${total}${noun ? ` ${noun}` : ''}`;
}

/** The leftmost stage of a row that calls for action — the row's ring marker. */
export function firstActionable(stages: Partial<Record<StageName, StageState>> | null | undefined): StageName | null {
  if (!stages) return null;
  return STAGES.find((s) => ACTIONABLE.has(stages[s] as StageState)) ?? null;
}

export function stageLabel(stage: StageName): string {
  return stage.toUpperCase();
}

/** Thousands separators, stable across locales so screenshots match. */
export function fmtInt(n: number): string {
  return n.toLocaleString('en-US');
}

/** "3 min ago" for the summary's computed_at — freshness is part of the claim. */
export function sinceLabel(iso: string, now: number = Date.now()): string {
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return 'just now';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min === 1) return '1 min ago';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  return h === 1 ? '1 h ago' : `${h} h ago`;
}
